import { capitalizeEachWord } from '../helper-functions/CapitalizeName'

/**
 * Change detection for the two card fields whose "was this edited?" answer isn't
 * a plain !== comparison. Both feed the card modal's save-on-close check, where
 * a false negative loses the user's edit and a false positive saves — and alerts
 * the whole board — for a card nobody touched.
 */

type Card = { due_date?: string; due_time?: string; assignees?: (string | { fullName?: string })[] }
type Option = { label: string }

// The card's stored due date as epoch ms, built exactly the way the picker seeds
// itself, so an untouched picker compares equal whatever precision the server
// returns due_time in.
export const cardDueDateMs = (card: Card): number | null =>
  card?.due_date && card?.due_time
    ? new Date(`${card.due_date}T${card.due_time}Z`).getTime()
    : null

// The picker holds a Date once set and '' once cleared.
export const dueDateChanged = (card: Card, startDate: Date | string | null | undefined): boolean =>
  (startDate ? new Date(startDate).getTime() : null) !== cardDueDateMs(card)

/**
 * Assignees are board-person options resolved by an async fetch: until it lands
 * the selection is empty, which would read as "everyone removed" — so an
 * unloaded option list means "not changed". The stored baseline is narrowed to
 * options that still exist, the same way useBoardPersons builds the selection,
 * so someone since removed from the board doesn't leave the card permanently
 * dirty.
 */
export const assigneesChanged = (card: Card, options: Option[], selected: Option[]): boolean => {
  if (!options?.length) return false
  const known = new Set(options.map((o) => o.label))
  const before = new Set<string>(
    (card?.assignees || [])
      .map((a) => capitalizeEachWord(typeof a === 'string' ? a : a?.fullName ?? ''))
      .filter((label: string) => known.has(label))
  )
  const now = new Set((selected || []).map((a) => a.label))
  return before.size !== now.size || [...before].some((label) => !now.has(label))
}
